/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* global DEFAULT_DX_VERSION */
import { action, computed, observable } from "mobx";
import { getLMServiceWorkerIframeSrc, getLMServiceWorkerScope } from "utils/LiquidModeUtils";
import {
  liquidModeDisqualificationSeverity,
  liquidModeNetworkProxyTechnology,
  liquidModeViewState,
  liquidModeError
} from "constants/enums";
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { subscribeToPDFEditOperations } from "../accessibility/AccessibilityManager";

/**
 * Variable names, imports and methods added to this class should be placed in
 * alphabetical order for uniformity.
 */
class LiquidModeViewStore {
  @observable isLiquidModeViewEnabled = false;
  @observable disqualificationSeverity = liquidModeDisqualificationSeverity.UNKNOWN;

  @observable networkProxyReady = false;
  @observable networkProxyTechnology = liquidModeNetworkProxyTechnology.UNKNOWN;
  @observable viewState = liquidModeViewState.DEFAULT;
  @observable error = null;

  @observable serviceWorkerScope = null;

  constructor() {
    this.init();
  }

  @action
  enableLiquidModeView() {
    this.isLiquidModeViewEnabled = true;
    initialPageLoadedPromise(this.stores).then(() => {
      this.isFileQualifiedForLiquidMode();
    });
  }

  enterLiquidMode() {
    return new Promise((resolve, reject) => {
      if (this.viewState === liquidModeViewState.EXITED) {
        this.setViewState(liquidModeViewState.LOADED);
        resolve(true);
      } else {
        if (this.shouldRunQualifier) {
          this.isFileQualifiedForLiquidMode()
            .then(disqualificationSeverity => {
              if (disqualificationSeverity !== liquidModeDisqualificationSeverity.HARD) {
                this.setViewState(liquidModeViewState.LOADING);
                resolve(true);
              } else {
                this.setError({ type: liquidModeError.PREPIPELINE_DISQUALIFICATION });
                resolve(false);
              }
            })
            .catch(_ => {
              this.setError({ type: liquidModeError.UNKNOWN_ERROR });
              reject(false);
            });
        } else {
          this.setViewState(liquidModeViewState.LOADING);
          resolve(true);
        }
      }
    });
  }

  exitLiquidMode(shouldTriggerTranslation = false) {
    if (shouldTriggerTranslation) {
      this.setViewState(liquidModeViewState.DEFAULT);
    } else {
      this.setViewState(liquidModeViewState.EXITED);
    }
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
      subscribeToPDFEditOperations(() => {
        this.shouldRunQualifier = true;
        this.exitLiquidMode(true);
      });
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.dxVersion = DEFAULT_DX_VERSION;

    this.isLiquidModeViewEnabled = false;
    this.disqualificationSeverity = liquidModeDisqualificationSeverity.UNKNOWN;
    this.shouldRunQualifier = true;

    this.networkProxyReady = false;
    this.networkProxyTechnology = liquidModeNetworkProxyTechnology.NONE;
    this.viewState = liquidModeViewState.DEFAULT;
    this.error = null;

    this.serviceWorkerScope = `${getLMServiceWorkerScope()}`;
    this.useMinifiedDX = true;
  }

  @computed
  get isFileQualified() {
    return (
      this.disqualificationSeverity !== liquidModeDisqualificationSeverity.HARD &&
      this.disqualificationSeverity !== liquidModeDisqualificationSeverity.UNKNOWN
    );
  }

  isFileQualifiedForLiquidMode() {
    return new Promise((resolve, reject) => {
      this.docApi.isFileQualifiedForLiquidMode().then(status => {
        const {
          data: { disqualificationSeverity }
        } = status;
        this.setDisqualificationSeverity(disqualificationSeverity);
        this.shouldRunQualifier = false;
        resolve(disqualificationSeverity);
      });
    });
  }

  @computed
  get liquidModeIframeSrc() {
    return this.networkProxyTechnology === liquidModeNetworkProxyTechnology.SERVICE_WORKERS
      ? getLMServiceWorkerIframeSrc(this.serviceWorkerScope, this.dxVersion, this.useMinifiedDX)
      : null;
  }

  @action
  setError(e) {
    this.error = e;
  }

  setDXVersion(dxVersion) {
    this.dxVersion = dxVersion;
  }

  @action
  setDisqualificationSeverity(severity) {
    this.disqualificationSeverity = severity;
  }

  @action
  setNetworkProxyReady(flag) {
    this.networkProxyReady = flag;
  }

  @action
  setNetworkProxyTechnology(technology) {
    this.networkProxyTechnology = technology;
  }

  setUseMinifiedDX(flag) {
    this.useMinifiedDX = flag;
  }

  @action
  setViewState(state) {
    this.viewState = state;
  }
}

export { LiquidModeViewStore };
